import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import StarRating from '../components/StarRating';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, Clock, User, Mail, Building, 
  FileText, CheckCircle, AlertCircle, Calendar, Star
} from 'lucide-react';
import api, { formatApiErrorDetail } from '../lib/api';

export default function QueryDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [query, setQuery] = useState(location.state?.query || null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [fbError, setFbError] = useState('');
  const [fbLoading, setFbLoading] = useState(false);

  useEffect(() => {
    api
      .get(`/queries/${id}`)
      .then((res) => setQuery(res.data))
      .catch(() => navigate('/dashboard'));
  }, [id, navigate]);

  const submitFeedback = async () => {
    if (rating < 1) {
      setFbError('Please select a star rating');
      return;
    }
    setFbError('');
    setFbLoading(true);
    try {
      const { data } = await api.post(`/queries/${id}/feedback`, { rating, comment });
      setQuery(data);
    } catch (err) {
      setFbError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setFbLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Resolved':
        return 'bg-green-100 text-green-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Resolved':
        return <CheckCircle className="h-5 w-5" />;
      case 'In Progress':
        return <Clock className="h-5 w-5" />;
      case 'Pending':
        return <AlertCircle className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!query) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 flex items-center space-x-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Dashboard</span>
        </Button>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-[#003366]">
              Query #{String(query.id).slice(0, 8).toUpperCase()}
            </h1>
            <div className="flex items-center space-x-2">
              <Badge className={getStatusColor(query.status)}>
                <span className="flex items-center space-x-1">
                  {getStatusIcon(query.status)}
                  <span>{query.status}</span>
                </span>
              </Badge>
              {query.priority === 'Priority' && (
                <Badge className="bg-red-100 text-red-800">Priority</Badge>
              )}
            </div>
          </div>
          <Badge variant="outline" className="text-[#0078D4] border-[#0078D4]">
            {query.queryType}
          </Badge>
        </div>

        {/* Query Information */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card className="shadow-lg">
              <CardHeader>
                <h2 className="text-xl font-bold text-[#003366]">Problem Description</h2>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {query.description}
                </p>
              </CardContent>
            </Card>

            {/* Response */}
            {query.response && (
              <Card className="shadow-lg border-l-4 border-l-blue-500">
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-blue-600" />
                    <h2 className="text-xl font-bold text-[#003366]">Expert Response</h2>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {query.response}
                  </p>
                  {query.resolvedAt && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-sm text-green-800 font-medium">
                        Resolved on {formatDate(query.resolvedAt)}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Feedback display / form */}
            {query.status === 'Resolved' && query.feedback && (
              <Card className="shadow-lg border-l-4 border-l-yellow-400" data-testid="feedback-display">
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <Star className="h-5 w-5 text-yellow-500" />
                    <h2 className="text-xl font-bold text-[#003366]">Your Feedback</h2>
                  </div>
                </CardHeader>
                <CardContent>
                  <StarRating value={query.feedback.rating} />
                  {query.feedback.comment && (
                    <p className="text-gray-700 italic mt-3">"{query.feedback.comment}"</p>
                  )}
                  <p className="text-sm text-gray-500 mt-2">Submitted on {formatDate(query.feedback.createdAt)}</p>
                </CardContent>
              </Card>
            )}

            {query.status === 'Resolved' && !query.feedback && user && query.userId === user.id && (
              <Card className="shadow-lg border-l-4 border-l-yellow-400" data-testid="feedback-form">
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <Star className="h-5 w-5 text-yellow-500" />
                    <h2 className="text-xl font-bold text-[#003366]">Rate This Resolution</h2>
                  </div>
                  <p className="text-sm text-gray-600">
                    How satisfied are you with the solution? Your feedback matters.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {fbError && (
                    <Alert variant="destructive" data-testid="feedback-error">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{fbError}</AlertDescription>
                    </Alert>
                  )}
                  <StarRating value={rating} onChange={setRating} size={6} />
                  <Textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Share your experience (optional)..."
                    className="min-h-[100px]"
                    data-testid="feedback-comment-input"
                  />
                  <Button
                    onClick={submitFeedback}
                    disabled={fbLoading}
                    className="bg-[#0078D4] hover:bg-[#005a9e]"
                    data-testid="feedback-submit-btn"
                  >
                    {fbLoading ? 'Submitting...' : 'Submit Feedback'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {query.status === 'Pending' && (
              <Card className="shadow-lg bg-yellow-50 border-yellow-200">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-3">
                    <Clock className="h-6 w-6 text-yellow-600" />
                    <div>
                      <p className="font-semibold text-yellow-900">
                        Your query is being reviewed
                      </p>
                      <p className="text-sm text-yellow-800">
                        You will receive a response within {query.responseTime}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {query.status === 'In Progress' && (
              <Card className="shadow-lg bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-3">
                    <Clock className="h-6 w-6 text-blue-600 animate-pulse" />
                    <div>
                      <p className="font-semibold text-blue-900">
                        Working on your query
                      </p>
                      <p className="text-sm text-blue-800">
                        Our expert is currently analyzing and preparing your solution
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Query Details */}
            <Card className="shadow-lg">
              <CardHeader>
                <h3 className="text-lg font-bold text-[#003366]">Query Details</h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <User className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-medium text-gray-900">{query.name}</p>
                  </div>
                </div>

                {query.companyName && (
                  <div className="flex items-start space-x-3">
                    <Building className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">Company</p>
                      <p className="font-medium text-gray-900">{query.companyName}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-start space-x-3">
                  <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium text-gray-900">{query.email}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Submitted</p>
                    <p className="font-medium text-gray-900">{formatDate(query.submittedAt)}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Response SLA</p>
                    <p className="font-medium text-gray-900">{query.responseTime}</p>
                  </div>
                </div>

                {query.estimatedResolution && query.status !== 'Resolved' && (
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">Expected By</p>
                      <p className="font-medium text-gray-900">{formatDate(query.estimatedResolution)}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <Card className="shadow-lg">
              <CardHeader>
                <h3 className="text-lg font-bold text-[#003366]">Need Help?</h3>
              </CardHeader>
              <CardContent className="space-y-3">
                <a href="mailto:phatak.kau@live.com">
                  <Button variant="outline" className="w-full">
                    <Mail className="h-4 w-4 mr-2" />
                    Contact Support
                  </Button>
                </a>
                <Button
                  onClick={() => navigate('/submit-query')}
                  className="w-full bg-[#0078D4] hover:bg-[#005a9e]"
                >
                  Submit Another Query
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
